import BaseComponent from "./base_component_controller";

import { useResize } from "hb/insights/use_resize";
import { useChartConfig } from "hb/insights/use_chart_config";
import {
  dimensionType,
  dimensionTypes,
  valueFormatter,
} from "hb/insights/chart";
import {
  nestedGroupByToMap,
  nestedGroupBy,
  foldedMap,
  extractCascade,
} from "hb/lib/collection_helpers";

export default class extends BaseComponent {
  static viewId = "bar";

  static get values() {
    return {
      ...BaseComponent.values,
      querySessionId: String,
      schema: Array,
      results: Array,
      chartConfig: Object,
    };
  }

  static configs = [
    "categoryField",
    "valueField",
    "valueFieldUnit",
    "labelField",

    { name: "groupType", default: "events" },

    "groupField",

    { name: "groups", default: "{}", type: "json" },
    { name: "horizontal", default: false, type: "boolean" },
    { name: "stacked", default: false, type: "boolean" },
  ];

  connect() {
    super.connect();

    useChartConfig(this);
  }

  outletsConnected() {
    super.outletsConnected();

    useResize(this, { element: this.outletComponent("chart").chartTarget });
    this.initQuery();
  }

  processConfig() {
    const { results, meta } = this.query;
    let { categoryField, valueField, groups, groupField, groupType } =
      this.config;

    let colorIdx = -1;
    const gatherColor = (n) => {
      colorIdx = (colorIdx + 1) % this.colorValues.length;
      return groups[n]?.color || this.colorValues[colorIdx];
    };

    let groupConfig;

    if (groupType === "fields") {
      const zDomain = meta.fields.filter((n) => n != categoryField);
      this.derivedConfig.zDomain = zDomain;

      groupConfig = zDomain.map((name, idx) => ({
        idx,
        name,
        disabled: this.groupDisabled(idx),
        color: gatherColor(name),
      }));
    } else if (groupField) {
      const zDomain = Array.from(
        new Set(results.map((d) => d.field(groupField))),
      );
      this.derivedConfig.zDomain = zDomain;
      groupConfig = zDomain.map((name, idx) => ({
        idx,
        name,
        disabled: this.groupDisabled(idx),
        color: gatherColor(name),
      }));
    } else {
      groupConfig = [
        {
          idx: 0,
          name: valueField,
          color: gatherColor(valueField),
        },
      ];
    }

    this.derivedConfig.groupConfig = groupConfig;
  }

  renderConfig() {
    const { meta } = this.query;

    ["categoryField", "valueField", "groupField"].forEach((name) => {
      let allFields;
      if (name === "groupField") {
        allFields = ["__empty", ...meta.fields];
      } else {
        allFields = meta.fields;
      }

      this.find(`[data-input-name="${name}"] ul`).innerHTML = allFields
        .map((field) =>
          this.comboboxItemTemplate({
            classes: this[name] === field ? "selected" : "",
            value: field,
            id: field,
            display: field,
            ...(field == "__empty" && {
              display: null,
              badge: "none",
            }),
          }),
        )
        .join("\n");
    });

    this.find(
      `input[name="groupType"][id="${this.config.groupType}"]`,
    ).checked = "true";
    this.find(`input[name="stacked"]`).checked = this.config.stacked;
    this.find(`input[name="horizontal"]`).checked = this.config.horizontal;
    this.find(`input[name="categoryField"]`).value =
      this.config.categoryField || "";
    this.find(`input[name="valueField"]`).value = this.config.valueField || "";
    this.find(`input[name="groupField"]`).value = this.config.groupField || "";

    this.renderFieldGroups(this.derivedConfig.groupConfig);
  }

  validate() {
    let { categoryField, valueField, groupField } = this.config;

    if (!categoryField || !valueField) {
      this.errorMessage =
        "Select fields in chart config or update data to fit chart";
    } else if (this.hasUnknownFields(categoryField, valueField, groupField)) {
      this.errorMessage = "Unknown field selected";
    }
  }

  queryContextDefaults() {
    const { meta } = this.query;
    let config = {};
    const fieldTypes = Object.entries(meta.fieldTypes);

    const xRes = extractCascade(fieldTypes, ([, t]) => t === "string");

    const yRes = extractCascade(
      xRes[1],
      ([, t]) => t === "integer" || t === "float",
      ([, t]) => t === "datetime",
    );

    if (xRes[0] !== undefined) config.categoryField = xRes[0][0];
    if (yRes[0] !== undefined) config.valueField = yRes[0][0];

    return config;
  }

  render() {
    let { meta, results } = this.query;
    let {
      categoryField,
      valueField,
      groupType,
      valueFieldUnit,
      labelField,
      groupField,
      horizontal,
      stacked,
    } = this.config;

    let dataset, series, zDomain, nestedLabels;
    let xAxis, yAxis;

    const barDefault = {
      type: "bar",
      animation: false,
    };

    let tooltip = {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
    };

    if (groupType === "fields") {
      const colNames = meta.fields.filter((n) => n != categoryField);

      dataset = {
        dimensions: [
          { name: categoryField, type: dimensionType(categoryField) },
          ...colNames,
        ],
        source: results.map((r) => r.slice(categoryField, ...colNames)),
      };

      series = this.derivedConfig.groupConfig
        .filter(({ disabled }) => !disabled)
        .map(({ name, color }) => ({
          ...barDefault,
          name,
          stack: stacked ? "default" : null,
          itemStyle: { color },
          dimensions: [categoryField, name],
        }));
    } else if (groupField) {
      zDomain = Array.from(new Set(results.map((d) => d.field(groupField))));

      const nested = nestedGroupByToMap(
        results,
        (r) => [r.field(categoryField), r.field(groupField)],
        (r) => r.field(valueField),
      );

      nestedLabels = nestedGroupBy(
        results,
        (r) => [r.field(categoryField), r.field(groupField)],
        (r) => r.field(labelField),
      );

      const folded = foldedMap(nested, categoryField);

      dataset = {
        source: folded,
        dimensions: [
          { name: categoryField, type: dimensionTypes[categoryField] },
          ...zDomain,
        ],
      };

      series = this.derivedConfig.groupConfig
        .filter(({ disabled }) => !disabled)
        .map(({ name, color }) => ({
          ...barDefault,
          name,
          itemStyle: { color },
          stack: stacked ? "default" : null,
          dimensions: [categoryField, name],
        }));
    } else {
      if (labelField) {
        dataset = {
          dimensions: [categoryField, valueField, labelField],
          source: results.map((r) =>
            r.slice(valueField, categoryField, labelField),
          ),
        };
      } else {
        dataset = {
          dimensions: [categoryField, valueField],
          source: results.map((r) => r.slice(valueField, categoryField)),
        };
      }
      series = [
        {
          ...barDefault,
          itemStyle: { color: this.derivedConfig.groupConfig[0].color },
          name: valueField,
        },
      ];
    }

    let valueFormatterFn = valueFormatter(valueFieldUnit);

    if (horizontal) {
      xAxis = {
        name: "",
      };

      if (valueFormatterFn) {
        xAxis.axisLabel = { formatter: valueFormatterFn };
        tooltip.valueFormatter = valueFormatterFn;
      }

      yAxis = {
        type: "category",
        name: "",
        nameLocation: "middle",
        axisLine: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
      };

      dataset.source.reverse();
    } else {
      yAxis = { name: "" };

      if (valueFormatterFn) {
        yAxis.axisLabel = { formatter: valueFormatterFn };
        tooltip.valueFormatter = valueFormatterFn;
      }

      xAxis = {
        type: "category",
        name: "",
      };
    }

    if (labelField) {
      series.forEach((s) => {
        s.label = {
          show: false,
          formatter: (d) => {
            if (groupField) {
              const { name } = d;
              const [, inner] = d.dimensionNames;
              return nestedLabels[name][inner];
            } else {
              return d.data[labelField];
            }
          },
          color: "white",
          position: "insideLeft",
        };
      });
    }

    const options = {
      ...this.defaultChartConfig,
      dataset,
      series,
      xAxis,
      yAxis,
      legend: null,
      tooltip: { ...this.defaultChartConfig.tooltip, ...tooltip },
    };

    this.outletComponent("chart").setChartOptions(options);
    this.outletComponent("legend").render(this.derivedConfig.groupConfig);
  }
}
